const CYRILLIC_TO_LATIN = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch',
  ы: 'y', э: 'e', ю: 'yu', я: 'ya', ь: '', ъ: '',
};

export function normalizeSearchText(value) {
  return (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function transliterateCyrillicToLatin(value) {
  if (!value) return '';
  return normalizeSearchText(value)
    .split('')
    .map((ch) => CYRILLIC_TO_LATIN[ch] ?? ch)
    .join('');
}

export function expandLatinVariants(value) {
  const base = normalizeSearchText(value);
  const variants = new Set([base]);
  if (base.includes('ks')) variants.add(base.replaceAll('ks', 'x'));
  if (base.includes('x')) variants.add(base.replaceAll('x', 'ks'));
  return variants;
}

export function getQueryVariants(searchValue) {
  const base = normalizeSearchText(searchValue);
  const variants = new Set([base]);
  const transliterated = transliterateCyrillicToLatin(base);
  if (transliterated) {
    expandLatinVariants(transliterated).forEach((v) => variants.add(v));
  }
  expandLatinVariants(base).forEach((v) => variants.add(v));
  return [...variants].filter(Boolean);
}

/**
 * Build a set of expanded search variants for a list of raw field values
 * (handles normalisation, Cyrillic transliteration, and ks/x expansion).
 */
export function buildFieldVariants(fieldValues) {
  const variants = new Set();
  fieldValues.filter(Boolean).forEach((raw) => {
    const norm = normalizeSearchText(raw);
    if (norm) variants.add(norm);
    const translit = transliterateCyrillicToLatin(raw);
    if (translit) variants.add(translit);
  });
  const expanded = new Set();
  variants.forEach((v) => {
    expanded.add(v);
    expandLatinVariants(v).forEach((ev) => expanded.add(ev));
  });
  return expanded;
}

/**
 * Returns true when at least one query variant matches at least one field variant (substring match).
 */
export function matchesSearch(searchValue, fieldValues) {
  const norm = normalizeSearchText(searchValue);
  if (!norm) return true;
  const queryVariants = getQueryVariants(searchValue);
  const userVariants = buildFieldVariants(fieldValues);
  return queryVariants.some((q) =>
    [...userVariants].some((v) => v.includes(q))
  );
}
